import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  Invoice,
  Participant,
  PaymentOption,
  TournamentTeam,
  TournamentTeamJerseyColors,
  TournamentTeamParticipantRegistration,
  TournamentTeamRole
} from '@/services/api'

import { useGlobalStore } from '@/stores/global'
import { useTournament } from '@/composable/useTournament'
import {
  getTournament,
  getTeamRegistrationProgress,
  saveTeamRegistrationProgress,
  getTournamentTeam
} from '@/helpers/tournament-helpers'
import type { TournamentTeamParticipantRow } from '@/utils/helpers-tournament'
import { DEFAULT_TOURNAMENTTEAMJERSEYCOLORS } from '@/services/defaults/allDefaults'
import { formatCurrency } from '@/composable/formatCurrency'

export interface TournamentTimeslotAvailability {
  id: number
  available: boolean
}

/** this needs to be aligned with the list of roles in TournamentRegistration **/
/** this need to be aligned with the setRegistrationFromTeam function below **/
export type ValidRoleCodes = 'players' | 'coaches' | 'umpires' | 'volunteers' | 'team_managers'

type RoleParticipantsMap = {
  [key in ValidRoleCodes]: TournamentTeamParticipantRow[]
}

interface TournamentRegistration extends RoleParticipantsMap {
  paymentOption: PaymentOption | undefined
  teamName: string
  teamId: number
  clubId: number
  tournamentId: number
  team_colors: TournamentTeamJerseyColors
}

export const useTourregStore = defineStore(
  'tourreg',
  () => {
    const team = ref<TournamentTeam>()
    const registration = ref<TournamentRegistration>()
    const cartValidationToken = ref<string>()
    const invoice = ref<Invoice>()
    const warnings = ref<Record<string, string[]>>({})

    const globalStore = useGlobalStore()

    function logout() {
      team.value = undefined
      registration.value = undefined
      cartValidationToken.value = ''
      invoice.value = undefined
      warnings.value = {}
    }

    function setTeam(tTeam: TournamentTeam) {
      team.value = tTeam
      setRegistrationFromTeam(tTeam)
    }

    function setRegistration(tRegistration: TournamentRegistration) {
      registration.value = tRegistration
    }

    function resetStore() {
      // TODO: dump cart in api?
      registration.value = undefined
      cartValidationToken.value = undefined
      invoice.value = undefined
      warnings.value = {}
      team.value = undefined
    }

    function getTeamColours() {
      return registration.value?.team_colors ?? DEFAULT_TOURNAMENTTEAMJERSEYCOLORS
    }

    function setTeamColours(obj: TournamentTeamJerseyColors) {
      if (!registration.value) {
        return
      }

      registration.value.team_colors = obj
    }

    function setPaymentOption(paymentOption: PaymentOption) {
      if (registration.value) {
        registration.value.paymentOption = paymentOption
      }
    }

    function getParticpantRowsByRole(role: TournamentTeamRole): TournamentTeamParticipantRow[] {
      const roleCode = role.code as ValidRoleCodes
      return registration.value?.[roleCode] ?? []
    }

    function setParticpantRowsByRole(
      role: TournamentTeamRole,
      rows: TournamentTeamParticipantRow[]
    ) {
      const roleCode = role.code as ValidRoleCodes
      // @ts-expect-error: roleCode is a valid key in registration.value
      registration.value[roleCode] = rows
    }

    function haveParticipantInList(
      role: TournamentTeamRole,
      tPlayer: TournamentTeamParticipantRegistration
    ): boolean {
      const rows = getParticpantRowsByRole(role)
      const matchIdx = rows.findIndex((item) => {
        if (item.participant.participant_number) {
          return tPlayer.participant_number === item.participant.participant_number
        }
      })

      if (matchIdx !== -1) {
        return true
      }

      return false
    }

    function addParticipantRow(
      role: TournamentTeamRole,
      tPlayerInfo: TournamentTeamParticipantRow
    ) {
      const participantRows = getParticpantRowsByRole(role)
      participantRows.push(tPlayerInfo)
      setParticpantRowsByRole(role, participantRows)

      globalStore.addSnackbarMessageToQueue(`Added ${role.name} to registration`, 'info')
    }
    function removeParticipantRow(
      role: TournamentTeamRole,
      participantIdx: number,
      tPlayerRow: TournamentTeamParticipantRegistration
    ) {
      const rows = getParticpantRowsByRole(role)
      if (
        rows[participantIdx] &&
        rows[participantIdx].participant.participant_number === tPlayerRow.participant_number
      ) {
        globalStore.addSnackbarMessageToQueue(
          'Removed player from registration: ' + tPlayerRow.first_name,
          'warning'
        )
        rows.splice(participantIdx, 1)
        setParticpantRowsByRole(role, rows)
      }
    }

    function convertParticipantToParticipantRow(p: Participant): TournamentTeamParticipantRow {
      return <TournamentTeamParticipantRow>{ status: {}, participant: p }
    }

    function setRegistrationFromTeam(tTeam: TournamentTeam) {
      registration.value = {
        paymentOption: undefined,
        teamName: tTeam.team_name,
        teamId: tTeam.id,
        clubId: tTeam.club?.id ?? 0,
        tournamentId: tTeam.tournament,

        /** be sure to keep this list of roles aligned with the validRoleTypes declared above **/
        players: tTeam.players.map((p) => convertParticipantToParticipantRow(p)) ?? [],
        coaches: tTeam.coaches.map((p) => convertParticipantToParticipantRow(p)) ?? [],
        umpires: tTeam.umpires.map((p) => convertParticipantToParticipantRow(p)) ?? [],
        volunteers: tTeam.volunteers.map((p) => convertParticipantToParticipantRow(p)) ?? [],
        team_managers: tTeam.team_managers?.map((p) => convertParticipantToParticipantRow(p)) ?? [],

        team_colors: DEFAULT_TOURNAMENTTEAMJERSEYCOLORS
      }
    }

    async function loadTeamFromDb(tournamentTeamId: number) {
      const teamData = await getTournamentTeam(tournamentTeamId)
      if (teamData) {
        const tteam = teamData as TournamentTeam
        setTeam(tteam)
        setRegistrationFromTeam(tteam)
      }
    }

    async function getRegistrationProgress(teamId: number) {
      const regInfo = await getTeamRegistrationProgress(teamId)
      if (regInfo) {
        registration.value = JSON.parse(regInfo)
      }
    }

    async function saveRegistrationProgress(teamId: number) {
      // const teamId = registration.value?.teamId ?? 0
      if (teamId) {
        await saveTeamRegistrationProgress(teamId, JSON.stringify(registration.value))
      }
    }

    function setRegistrationInvoice(tInvoice: Invoice) {
      console.log(['setRegistrationInvoice', tInvoice])
      invoice.value = tInvoice
    }

    function setRegistrationWarnings(tWarns: Record<string, string[]>) {
      console.log(['setRegistrationWarnings', tWarns])
      warnings.value = tWarns
    }

    async function fetchTournamentFromRegistration() {
      const tournamentId = registration.value?.tournamentId ?? undefined
      if (!tournamentId) {
        return
      }

      const { tournament, setTournament } = useTournament()
      const curTourId = tournament.value?.id ?? 0
      if (curTourId !== tournamentId) {
        const tournament = await getTournament(tournamentId)
        if (tournament) {
          setTournament(tournament)

          return tournament
        }
      }
    }

    function getInvoiceTotal() {
      const totals = getInvoiceTotals()
      return Number(totals.total)
    }

    const getInvoiceItems = computed(() => {
      return (
        invoice.value?.item_rows?.map((item) => ({
          ...item,
          national_price: formatCurrency(item.national_price),
          provincial_price: formatCurrency(item.provincial_price),
          cost: formatCurrency(item.cost),
          cost_tax: formatCurrency(item.cost)
        })) ?? []
      )
    })

    function getInvoiceTotals() {
      return {
        subtotal: formatCurrency(invoice.value?.subtotal ?? 0),
        taxes: formatCurrency(invoice.value?.taxes ?? 0),
        fees: formatCurrency(invoice.value?.fees ?? 0),
        // @todo tcropley: is this value
        // outstanding: invoice.value?.outstanding ?? 0,
        outstanding: 0,
        total: formatCurrency(invoice.value?.total ?? 0)
      }
    }

    // function cvrFromInvoice() {
    //   alert('In this function --- cvrFromInvoice // tourreg.ts ---')
    //
    //   const teamId = registration.value?.teamId ?? 0
    //   const cvr: CartValidationRequest = {}
    //   // @todo tcropley: ??
    //
    //   console.log(['tourreg.ts', teamId, invoice.value ?? 'no invoice'])
    //
    //   cvr.items = invoice.value?.invoice?.item_rows
    //     ?.filter((item: InvoiceRow) => {
    //       return !item.credit
    //     })
    //     .map((item) => item.cart_item as CartItem)
    //
    //   console.log(['tourreg.ts -- cvr', cvr])
    //
    //   return cvr
    // }
    //
    // async function processInvoice(
    //   payment_ott: string,
    //   billingInfo: Billing
    // ): Promise<PaymentResult> {
    //   console.log({
    //     where: 'processInvoice',
    //     team: registration.value?.teamId ?? 0,
    //     payment: payment_ott,
    //     billingInfo: billingInfo,
    //     invoice: invoice.value ?? 'no invoice'
    //   })
    //
    //   if (!invoice.value) {
    //     return {
    //       status: false,
    //       documents: false,
    //       message: 'No cart'
    //     }
    //   }
    //
    //   let success = false
    //   let paymentMessage = ''
    //   let documents = false
    //
    //   const teamId = registration.value?.teamId ?? 0
    //   // cart contents
    //   const tempCart = cvrFromInvoice()
    //
    //   console.log([tempCart, payment_ott, billingInfo])
    //
    //   return {
    //     status: false,
    //     documents: false,
    //     message: 'BAILED ON PURPOSE in processInvoice'
    //   }
    //
    //   try {
    //     processingCartData.value = true
    //     const response = await api.api.registerTournamentTeam(team.value?.id ?? 0, {
    //       data: {
    //         items: tempCart.items,
    //         // cart_validation_token: invoice.value?.cart_validation_token,
    //         payment_ott: payment_ott,
    //         billing: billingInfo
    //       }
    //     })
    //
    //     documents = response.data.data.documents ?? false
    //
    //     console.log(documents)
    //     success = true
    //     paymentMessage = 'Thank you for your payment'
    //   } catch (error) {
    //     success = false
    //     paymentMessage = 'We were unable to process your payment'
    //   } finally {
    //     processingCartData.value = false
    //   }
    //
    //   return {
    //     message: paymentMessage,
    //     status: success,
    //     documents: documents
    //   }
    // }

    /**
     * Creates a tournament team validation request by extracting data
     * from the current registration value and fetching relevant participants.
     *
     * @return {Promise<Object|undefined>} A promise resolving to an object containing
     *                                     team validation request details including
     *                                     participants, team colors, and payment option
     *                                     if the registration value exists. Otherwise,
     *                                     returns `undefined`.
     */
    async function createTournamentTeamValidationRequest() {
      if (!registration.value) {
        return undefined
      }
      // export interface TournamentCartRequestValidate {

      // const tournament = await fetchTournamentFromRegistration()
      return {
        participants: await participantsFromRegistration(),
        team_colors: registration.value.team_colors,
        payment_option: registration.value.paymentOption?.id ?? 0
      }
    }

    /**
     * Retrieves the list of participants from the tournament team registration.
     * Participants can include players, coaches, umpires, and volunteers if available in the registration details.
     *
     * @return {TournamentTeamParticipantRegistration[]} The list of participants extracted from the registration data.
     */
    async function participantsFromRegistration() {
      const tournamentId = registration.value?.tournamentId ?? 0

      const { tournament, fetchTournament } = useTournament()

      if (!tournament.value || tournament.value.id != tournamentId) {
        fetchTournament(tournamentId)
      }

      if (!tournament.value) {
        console.error('No tournament found for team: ' + tournamentId)
        return []
      }

      const tournamentRoles = tournament.value.roles ?? []

      let participants: TournamentTeamParticipantRegistration[] = []

      tournamentRoles.forEach((role) => {
        const p = getParticpantRowsByRole(role)
        if (p.length) {
          participants = [
            ...participants,
            ...p.map((item) => {
              return item.participant
            })
          ]
        }
      })

      return participants
    }

    return {
      team,
      registration,
      logout,
      cartValidationToken,
      invoice,
      warnings,
      getInvoiceTotal,
      getInvoiceTotals,
      getInvoiceItems,
      // processInvoice,
      setTeam,
      setRegistration,
      setRegistrationFromTeam,
      // get, store registration process
      loadTeamFromDb,
      getRegistrationProgress,
      saveRegistrationProgress,
      addParticipantRow,
      removeParticipantRow,
      haveParticipantInList,
      createTournamentTeamValidationRequest,
      participantsFromRegistration,
      getTeamColours,
      setTeamColours,
      setPaymentOption,
      setRegistrationInvoice,
      setRegistrationWarnings,
      fetchTournamentFromRegistration,
      resetStore
    }
  },
  {
    persist: true
  }
)
